<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { DEFAULT_TEXT } from '../../depth/text3d';
  import { t } from '../../i18n/index.svelte';
  import { depthToRgba, downloadBlob, drawToCanvas } from '../../image';
  import { bakeModel, bakeText } from '../../scene/bake';
  import { fitDepthRange } from '../../scene/fit';
  import { initGeometry } from '../../scene/geometry';
  import { renderSceneDepth } from '../../scene/render';
  import { sceneStore, starterScene } from '../../scene/store.svelte';
  import {
    CAMERA_TRACK,
    defaultShape,
    defaultTransform,
    emptyScene,
    newId,
    type SceneDesc,
    type SceneNode,
    type ShapeKind,
  } from '../../scene/types';
  import { Viewport, type GizmoMode } from '../../scene/viewport';
  import FileButton from '../ui/FileButton.svelte';
  import Section from '../ui/Section.svelte';
  import CameraProps from './CameraProps.svelte';
  import NodeProps from './NodeProps.svelte';
  import Outliner from './Outliner.svelte';
  import Timeline from './Timeline.svelte';

  let { onuse }: { onuse: (target: 'studio' | 'animate') => void } = $props();

  let canvas: HTMLCanvasElement;
  let depthCanvas: HTMLCanvasElement;
  let viewport: Viewport | null = null;
  let ready = $state(false);
  let gizmo = $state<GizmoMode>('translate');
  let throughCamera = $state(false);
  let error = $state('');

  const selectedNode = $derived(sceneStore.current.nodes.find((n) => n.id === sceneStore.selected) ?? null);

  onMount(async () => {
    await initGeometry();
    viewport = new Viewport(canvas, {
      select: (id) => (sceneStore.selected = id),
      transform: (id, tr) => commitTransform(id, tr),
    });
    ready = true;
    viewport.setScene(sceneStore.current);
    viewport.focus();
    window.addEventListener('keydown', onKey);
  });

  onDestroy(() => {
    viewport?.dispose();
    window.removeEventListener('keydown', onKey);
  });

  $effect(() => {
    const s = sceneStore.current;
    if (ready) viewport?.setScene(s);
  });
  $effect(() => {
    const time = sceneStore.time;
    if (ready) viewport?.setTime(time);
  });
  $effect(() => {
    const sel = sceneStore.selected;
    if (ready) viewport?.select(sel === CAMERA_TRACK ? null : sel);
  });
  $effect(() => {
    if (ready) viewport?.setGizmoMode(gizmo);
  });
  $effect(() => {
    if (viewport) viewport.viewThroughCamera = throughCamera;
  });

  // Live depth preview of the scene camera (debounced).
  let previewTimer: ReturnType<typeof setTimeout> | undefined;
  $effect(() => {
    const s = sceneStore.current;
    const time = sceneStore.time;
    if (!ready) return;
    clearTimeout(previewTimer);
    previewTimer = setTimeout(() => {
      try {
        drawToCanvas(depthCanvas, depthToRgba(renderSceneDepth(s, time, 320, 180)));
      } catch (e) {
        error = String(e);
      }
    }, 120);
  });

  /** Gizmo edits: animated nodes get a key at the current time, others change their base transform. */
  function commitTransform(id: string, tr: SceneNode['transform']) {
    const time = Math.round(sceneStore.time * 100) / 100;
    sceneStore.update((d) => {
      const track = d.timeline.tracks.find((x) => x.target === id);
      if (track?.keys.length) {
        track.keys = track.keys.filter((k) => Math.abs(k.time - time) > 1e-3);
        track.keys.push({ time, position: tr.position, rotation: tr.rotation, scale: tr.scale, easing: 'ease_in_out' });
      } else {
        const n = d.nodes.find((x) => x.id === id);
        if (n) n.transform = tr;
      }
    });
  }

  function onKey(e: KeyboardEvent) {
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) sceneStore.redo();
      else sceneStore.undo();
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      sceneStore.redo();
    } else if (e.key === 'w') gizmo = 'translate';
    else if (e.key === 'e') gizmo = 'rotate';
    else if (e.key === 'r') gizmo = 'scale';
    else if (e.key === 'f') viewport?.focus();
  }

  const KINDS: ShapeKind[] = ['box', 'sphere', 'torus', 'cylinder', 'plane', 'terrain', 'heightmap', 'group'];

  function addNode(kind: ShapeKind) {
    const id = newId(kind.slice(0, 3));
    sceneStore.update((d) => {
      d.nodes.push({
        id,
        name: t(`scene.shape.${kind}`),
        parent: null,
        transform: defaultTransform(),
        visible: true,
        shape: defaultShape(kind),
      });
    });
    sceneStore.selected = id;
  }

  async function addText() {
    error = '';
    try {
      const params = { ...DEFAULT_TEXT, text: 'TEXT' };
      const mesh = await bakeText(params);
      const id = newId('txt');
      sceneStore.update((d) => {
        d.meshes.push(mesh);
        d.nodes.push({
          id,
          name: t('source.text'),
          parent: null,
          transform: defaultTransform(),
          visible: true,
          shape: { kind: 'mesh', mesh: mesh.id },
          source: { kind: 'text', text: { ...params } },
        });
      });
      sceneStore.selected = id;
    } catch (e) {
      error = String(e);
    }
  }

  async function importModel(f: File) {
    error = '';
    try {
      const mesh = await bakeModel(f);
      const id = newId('obj');
      sceneStore.update((d) => {
        d.meshes.push(mesh);
        d.nodes.push({
          id,
          name: f.name,
          parent: null,
          transform: defaultTransform(),
          visible: true,
          shape: { kind: 'mesh', mesh: mesh.id },
          source: { kind: 'import', file: f.name },
        });
      });
      sceneStore.selected = id;
    } catch (e) {
      error = t('scene.importError', { msg: e instanceof Error ? e.message : String(e) });
    }
  }

  function groupSelected() {
    const sel = selectedNode;
    if (!sel) return;
    const gid = newId('grp');
    sceneStore.update((d) => {
      d.nodes.push({
        id: gid,
        name: t('scene.shape.group'),
        parent: sel.parent,
        transform: defaultTransform(),
        visible: true,
        shape: { kind: 'group' },
      });
      const n = d.nodes.find((x) => x.id === sel.id)!;
      n.parent = gid;
    });
    sceneStore.selected = gid;
  }

  function fitDepth() {
    const r = fitDepthRange(sceneStore.current);
    if (r) sceneStore.update((d) => ((d.camera.near = +r.near.toFixed(3)), (d.camera.far = +r.far.toFixed(3))));
  }

  function save() {
    downloadBlob(new Blob([JSON.stringify(sceneStore.current, null, 1)], { type: 'application/json' }), 'scene.json');
  }

  async function loadExample(name: string) {
    error = '';
    try {
      const r = await fetch(`/examples/${name}.scene.json`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      sceneStore.replace({ ...emptyScene(), ...((await r.json()) as SceneDesc) });
      viewport?.focus();
    } catch (e) {
      error = t('scene.loadError', { msg: e instanceof Error ? e.message : String(e) });
    }
  }

  async function load(f: File) {
    error = '';
    try {
      const s = JSON.parse(await f.text()) as SceneDesc;
      if (!s || !Array.isArray(s.nodes) || !s.camera) throw new Error('not a scene file');
      sceneStore.replace({ ...emptyScene(), ...s });
      viewport?.focus();
    } catch (e) {
      error = t('scene.loadError', { msg: e instanceof Error ? e.message : String(e) });
    }
  }
</script>

<div class="editor">
  <aside class="left">
    <Section title={t('scene.add')}>
      <div class="adds">
        {#each KINDS as k (k)}
          <button onclick={() => addNode(k)} data-testid={`add-${k}`}>{t(`scene.shape.${k}`)}</button>
        {/each}
        <button onclick={addText} data-testid="add-text">{t('source.text')}</button>
      </div>
      <FileButton label={t('scene.import')} accept=".glb,.gltf,.stl,.obj" onfile={importModel} testid="import-model" />
    </Section>
    <Section title={t('scene.outliner')}>
      <Outliner />
      {#if selectedNode}
        <button class="small" onclick={groupSelected}>{t('scene.group')}</button>
      {/if}
    </Section>
  </aside>

  <main>
    <div class="toolbar row">
      <div class="seg">
        {#each ['translate', 'rotate', 'scale'] as const as m (m)}
          <button class:on={gizmo === m} onclick={() => (gizmo = m)} title={t(`scene.gizmo.${m}`)}
            >{t(`scene.gizmo.${m}`)}</button
          >
        {/each}
      </div>
      <button class:active={throughCamera} onclick={() => (throughCamera = !throughCamera)} data-testid="view-camera"
        >🎥 {t('scene.throughCamera')}</button
      >
      <button onclick={() => viewport?.focus()} title="F">{t('scene.focus')}</button>
      <button disabled={!sceneStore.canUndo} onclick={() => sceneStore.undo()} title="Ctrl+Z" data-testid="undo"
        >↶</button
      >
      <button disabled={!sceneStore.canRedo} onclick={() => sceneStore.redo()} title="Ctrl+Shift+Z" data-testid="redo"
        >↷</button
      >
      <span class="grow"></span>
      <button onclick={() => sceneStore.replace(starterScene())}>{t('scene.new')}</button>
      <button onclick={() => loadExample('castle')} data-testid="example-castle">🏰 {t('scene.exampleCastle')}</button>
      <FileButton label={t('scene.open')} accept=".json,application/json" onfile={load} testid="scene-open" />
      <button onclick={save} data-testid="scene-save">{t('scene.save')}</button>
      <button class="primary" onclick={() => onuse('studio')} data-testid="scene-to-studio"
        >{t('scene.toStudio')}</button
      >
      <button class="primary" onclick={() => onuse('animate')} data-testid="scene-to-animate"
        >{t('scene.toAnimate')}</button
      >
    </div>
    {#if error}<div class="error small banner">{error}</div>{/if}
    <div class="viewport">
      <canvas bind:this={canvas} data-testid="scene-viewport"></canvas>
      <div class="hint muted">{t('scene.viewportHint')}</div>
    </div>
    <Timeline />
  </main>

  <aside class="right">
    <Section title={sceneStore.selected === CAMERA_TRACK ? t('scene.camera') : t('scene.properties')}>
      {#if sceneStore.selected === CAMERA_TRACK}
        <CameraProps onfit={fitDepth} />
      {:else if selectedNode}
        {#key selectedNode.id}
          <NodeProps node={selectedNode} />
        {/key}
      {:else}
        <p class="muted small">{t('scene.selectHint')}</p>
      {/if}
    </Section>
    <Section title={t('scene.depthPreview')}>
      <canvas class="depth" bind:this={depthCanvas} data-testid="scene-depth-preview"></canvas>
      <button onclick={fitDepth}>{t('scene.fitDepth')}</button>
    </Section>
  </aside>
</div>

<style>
  .editor {
    display: grid;
    grid-template-columns: 260px 1fr 300px;
    height: 100%;
    min-height: 0;
  }
  aside {
    background: var(--panel);
    overflow-y: auto;
  }
  .left {
    border-right: 1px solid var(--line);
  }
  .right {
    border-left: 1px solid var(--line);
  }
  main {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }
  .toolbar {
    padding: 8px 12px;
    border-bottom: 1px solid var(--line);
    background: var(--panel);
    flex-wrap: wrap;
  }
  .toolbar :global(.drop) {
    padding: 5px 10px;
  }
  .toolbar .seg button {
    font-size: 12px;
  }
  .active {
    border-color: var(--accent);
    color: var(--accent);
  }
  .grow {
    flex: 1;
  }
  .viewport {
    flex: 1;
    min-height: 0;
    position: relative;
  }
  .viewport canvas {
    width: 100%;
    height: 100%;
    display: block;
  }
  .hint {
    position: absolute;
    left: 10px;
    bottom: 8px;
    font-size: 11px;
    pointer-events: none;
  }
  .adds {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .adds button {
    font-size: 12px;
    padding: 5px 6px;
  }
  .depth {
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
    border-radius: 4px;
  }
  .small {
    font-size: 12px;
    margin: 0;
  }
  .banner {
    padding: 4px 12px;
  }
  @media (max-width: 1100px) {
    .editor {
      grid-template-columns: 220px 1fr;
    }
    .right {
      grid-column: 1 / -1;
    }
  }
</style>
